/** Opt-in, page-local performance evidence. No transport, payload or persistent storage. */
export interface ResourceSample {
  path: string;
  startMs: number;
  durationMs: number;
  ttfbMs: number;
  transferBytes: number;
}
const round = (value: number) => Math.round(Math.max(0, Number.isFinite(value) ? value : 0) * 10) / 10;
const publicPath = (pathname: string) => pathname.replace(/\b[0-9a-f]{40,64}\b/gi, ':id').replace(/\b[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}\b/gi, ':id');

export function catalogueIsReady(main: Element): boolean {
  const catalogue = main.querySelector('[data-catalog-state]');
  return !catalogue || catalogue.getAttribute('data-catalog-state') === 'ready';
}

export function resourceSample(entry: Pick<PerformanceResourceTiming, 'name' | 'startTime' | 'duration' | 'requestStart' | 'responseStart' | 'transferSize' | 'initiatorType'>, origin: string): ResourceSample | null {
  let url: URL;
  try { url = new URL(entry.name); } catch { return null; }
  if (url.origin !== origin || !url.pathname.startsWith('/api/') || entry.initiatorType !== 'fetch') return null;
  return { path: publicPath(url.pathname), startMs: round(entry.startTime), durationMs: round(entry.duration), ttfbMs: round(entry.responseStart - entry.requestStart), transferBytes: round(entry.transferSize) };
}

export function installPerformanceAudit(): void {
  if (new URLSearchParams(location.search).get('perf_audit') !== '1' || document.getElementById('travel-perf-audit')) return;
  const root = document.getElementById('root');
  if (!root || typeof PerformanceObserver === 'undefined') return;
  const sink = document.createElement('output');
  sink.id = 'travel-perf-audit'; sink.hidden = true; document.body.append(sink);
  const resources: ResourceSample[] = [];
  const interactions: number[] = [];
  const routes: Array<{ path: string; readyMs: number; alerts: number }> = [];
  let lcpMs = 0; let cls = 0; let inputEvents = 0;
  let key = location.pathname + location.search; let routeStart = 0; let recorded = false;
  const supported = PerformanceObserver.supportedEntryTypes;
  const emit = () => {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    sink.textContent = JSON.stringify({ contract: 'travel-page-perf-v1', resources, routes, inputEvents, interactions, lcpMs, cls, supported: { event: supported.includes('event'), lcp: supported.includes('largest-contentful-paint'), layoutShift: supported.includes('layout-shift') }, navigation: nav ? { domContentLoadedMs: round(nav.domContentLoadedEventEnd), loadMs: round(nav.loadEventEnd) } : null });
  };
  const observe = (type: string, read: (entry: PerformanceEntry) => void) => {
    if (!supported.includes(type)) return;
    const observer = new PerformanceObserver((list) => { for (const entry of list.getEntries()) read(entry); emit(); });
    observer.observe({ type, buffered: true, ...(type === 'event' ? { durationThreshold: 16 } : {}) });
  };
  observe('resource', (entry) => { const sample = resourceSample(entry as PerformanceResourceTiming, location.origin); if (sample) { resources.push(sample); if (resources.length > 200) resources.shift(); } });
  observe('event', (entry) => { interactions.push(round(entry.duration)); if (interactions.length > 200) interactions.shift(); });
  observe('largest-contentful-paint', (entry) => { lcpMs = round(entry.startTime); });
  observe('layout-shift', (entry) => { const shift = entry as PerformanceEntry & { value: number; hadRecentInput: boolean }; if (!shift.hadRecentInput) cls = Math.round((cls + shift.value) * 10000) / 10000; });
  for (const type of ['pointerup', 'keydown']) document.addEventListener(type, () => { inputEvents += 1; emit(); }, { passive: true });
  let timer = 0;
  const check = () => {
    const next = location.pathname + location.search;
    if (next !== key) { key = next; routeStart = performance.now(); recorded = false; }
    if (recorded) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const main = root.querySelector('main');
      if (!main?.querySelector('h1') || root.querySelector('[aria-busy="true"],.events-list-loading')) return;
      if (!catalogueIsReady(main)) return;
      const pending = [...main.querySelectorAll('[role=status]')].some((node) => /загружа|проверяем|собираем|уточняем|получаем|строим/i.test(node.textContent ?? ''));
      if (pending || /загружаем экран|загружаем каталог|цены загружаются|количество результатов уточняется/i.test(main.textContent ?? '')) return;
      requestAnimationFrame(() => { if (recorded) return; recorded = true; routes.push({ path: publicPath(location.pathname), readyMs: round(performance.now() - routeStart), alerts: main.querySelectorAll('[role=alert]').length }); if (routes.length > 30) routes.shift(); emit(); });
    }, 100);
  };
  new MutationObserver(check).observe(root, { childList: true, subtree: true, attributes: true });
  check(); emit();
}
